import { datadogRum } from '@datadog/browser-rum'
import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import * as UploadActions from '@disco/state-management/actions/UploadActions'
import { getUpload } from '@disco/state-management/selectors/inbox'
import { pushUINotification } from '@disco/utils/notifications'
import { prepareTempItems, taskRunner, verifyFiles } from '@disco/utils/upload'
import get from 'lodash/get'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useParams, useLocation, Outlet } from 'react-router-dom'
import type { BusinessData } from 'types/business'
import type { DiscoPlaylist, InboxGroup, InboxPlaylist } from 'types/inbox'
import type { Location } from 'react-router-dom'
import BackgroundImage from 'components/common/background-image'
import UINotificationSystem from 'components/common/ui-notification-system'
import CookieBanner from 'components/modals/cookie-banner'
import PublicPage from 'components/public/page'
import FileDropWrapper from 'containers/FileDropWrapper'
import NotificationManager from '@disco/universal-components/NotificationManager'
import FileDropOverlay from './file-drop-overlay'
import FileReceiver from './file-receiver'
import PlayerContainer from './player-container'
import UploadFooterInbox from './upload-footer-inbox'
import UploadFooter from './upload-footer'
import {
  withTranslation,
  type WithTranslation,
} from '@disco/i18n/with-translation'

function mapStateToProps(state) {
  return {
    playlist: getUpload(state),
    businessData: window.business_data,
    group: window.group_data,
    businessList: state.upload.businessList,
    discoUser: state.upload.discoUser,
    discoPlaylist: state.upload.discoPlaylist,
    app: state.app,
  }
}

const mapDispatchToProps = {
  ...UploadActions,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

interface OwnProps {
  params: {
    groupId: string
    groupName: string
  }
  location: Location
}

type Props = PropsFromRedux & OwnProps & WithTranslation

class Upload extends React.Component<Props> {
  _confirmExit = e => {
    const { tempItems, sent } = this.props.playlist

    if (sent || tempItems.length === 0) {
      return null
    }

    const dialogText = this.props.t(
      'inboxes:unsentFilesOnLeave',
      "You haven't sent your files yet. Are you sure you want to leave the page?",
    )
    // custom beforeunload messages are removed from modern browsers, but some browser might still support it
    // eslint-disable-next-line no-param-reassign
    e.returnValue = dialogText
    return dialogText
  }

  componentDidMount() {
    datadogRum.setGlobalContextProperty('funnel', 'public-inbox-upload')
    datadogRum.addAction('Public inbox upload landed')
    window.addEventListener('beforeunload', this._confirmExit)
  }

  componentWillUnmount() {
    taskRunner.cancelTasks()
    window.removeEventListener('beforeunload', this._confirmExit)
    datadogRum.removeGlobalContextProperty('funnel')
  }

  componentDidUpdate(prevProps: Props) {
    const prev = prevProps.playlist
    const curr = this.props.playlist

    if (!prev.sending && curr.sending) {
      datadogRum.addAction('Public inbox upload submit attempted', {
        mode: this.props.discoPlaylist ? 'own_playlist' : 'files',
      })
    }

    if (!prev.sent && curr.sent) {
      datadogRum.addAction('Public inbox upload submitted')
    }

    if (prev.error !== curr.error && curr.error) {
      datadogRum.addAction('Public inbox upload submit failed', {
        error:
          curr.error instanceof Error
            ? curr.error.message
            : JSON.stringify(curr.error),
      })
    }
  }

  _onFileSelect = (files: File[]) => {
    mixpanel.sendPureEvent('Upload files via file selection dialog (Inbox)')
    datadogRum.addAction('Public inbox upload files added', {
      method: 'dialog',
      fileCount: files.length,
    })

    this._uploadFiles(files)
  }
  _onFileDrop = (files: File[]) => {
    mixpanel.sendPureEvent('Upload files via drag and drop (Inbox)')
    datadogRum.addAction('Public inbox upload files added', {
      method: 'drop',
      fileCount: files.length,
    })

    this._uploadFiles(files)
  }
  _uploadFiles = (files: File[]) => {
    // Prepare temp items and verify files
    const tempItems = verifyFiles(prepareTempItems(files), pushUINotification)
    this.props.insertUploadTempItems(tempItems)
    return api
      .getCSRFToken()
      .then(csrfToken => {
        tempItems.forEach(tempItem => {
          taskRunner.addTask({
            id: tempItem.tempId,
            func: this.props.uploadFileToDisco,
            params: [csrfToken, tempItem],
          })
        })
      })
      .catch(error => console.log(error))
  }
  _onUploadCancel = item => {
    taskRunner.removeTask(item.tempId)
    this.props.removeUploadTempItem(item)
  }

  render() {
    return (
      <FileDropWrapper
        overlay={
          <FileDropOverlay targetText={this.props.group.name ?? 'Inbox'} />
        }
        onDrop={this._onFileDrop}
        disabled={!!this.props.discoPlaylist}
        multiple
      >
        <PublicPage fullHeight backgroundWhite>
          <CookieBanner location="Upload" />
          {!this.props.playlist.sent && <BackgroundImage />}
          {/* @ts-ignore */}
          <FileReceiver
            formCaption={this.props.t(
              'inboxes:sendFilesTo',
              'Send files to {{inboxName}}',
              {
                inboxName:
                  this.props.group.name ??
                  this.props.t('inboxes:inbox', 'Inbox'),
              },
            )}
            customMessage={this.props.group.custom_message_html}
            onFileSelect={this._onFileSelect}
            onUploadCancel={this._onUploadCancel}
            submitButtonCaption={{
              caption: this.props.t('inboxes:sendFiles', 'Send files'),
              progress: this.props.t('inboxes:sendingFiles', 'Sending files'),
            }}
            inbox={this.props.group}
            {...this.props}
            params={{ ...this.props.params, path: 'upload' }}
          />
          {this.props.playlist.sent ? (
            <UploadFooterInbox
              displayLogo={
                this.props.businessData.public_metadata?.show_disco_logo
              }
              businessName={this.props.businessData.name}
            />
          ) : (
            <UploadFooter
              displayLogo={
                this.props.businessData.public_metadata?.show_disco_logo
              }
            />
          )}
          <PlayerContainer />
          <NotificationManager location={this.props.location} />
          <UINotificationSystem />
        </PublicPage>
        <Outlet />
      </FileDropWrapper>
    )
  }
}

const ConnectedUpload = connector(withTranslation('inboxes')(Upload))

// Wrapper component to provide router params to the class component
const UploadWithRouter = (props: any) => {
  const params = useParams<{ groupId: string; groupName: string }>()
  const location = useLocation()

  return <ConnectedUpload {...props} params={params} location={location} />
}

export default UploadWithRouter
